import { VFXEffectConfig } from '../../VFXEffectRegistry';
import { pulsingShell, orbiter, driftMotes, groundRing, carousel } from './statusAuraBuilders';

// =============================================================================
// STATUS AURAS — bespoke per-status visual language (u0 P2 "buff flat-tint")
// =============================================================================
//
// Server statuses (burn/poison/slow/frozen/stun/regen) used to render as ONE
// shape — `defensive_aura` flat-tinted per status (statusAura.config). Every
// status read as "some colored ring". These defs give each status a distinct
// silhouette so it reads at gameplay zoom without color vision:
//   burn   = ember ring + rising heat sparks        poison = dripping bubbles
//   slow   = heavy squashed drag ring (slow tweens)  frozen = crystalline shards
//   stun   = orbiting stars overhead                 regen  = rising green motes
//
// Composed from statusAuraBuilders (2026-07-17 modularization) — the shared
// grammar (shell/orbiter/motes/ring) is one line of intent each; per-status
// one-offs (poison's bubble pop, frozen's shard ring) stay literal. A new
// status is ~5 builder lines. Output-preserving on extraction (canonical
// JSON lock at refactor time; silhouette/palette invariants in the test).
//
// StatusVFXHandler.resolveStatusEffectId checks the registry for a bespoke id
// FIRST, so these win over the tinted fallback automatically once registered.
// All are category 'status', followTarget 'player' (FollowerSystem attaches
// them to the moving target). Multi-tone palettes are protected from play-time
// tint overrides by VFXEffectPlayer's ≥2-distinct-fills palette lock.

const STATUS_BURN: VFXEffectConfig = {
  id: 'status_burn',
  name: 'Burning',
  category: 'status',
  description: 'Ember ring + rising heat sparks for the burn DoT',
  followTarget: 'player',
  primitives: [
    // Heat-shimmer core (VQA 2026-07-17: alphas raised — the first pass got
    // lost against bright sprites; additive-over-dark needs saturation).
    pulsingShell({
      width: 56, height: 64, color: 0xFF4500, alpha: 0.32, glow: true,
      stroke: { color: 0xFF7733, width: 2 },
      pulse: { scale: 1.12, alpha: 0.2, duration: 380 },
    }),
    // Orbiting embers
    orbiter({
      radius: 4, color: 0xFF6622, alpha: 1, glow: true,
      orbit: { radius: 30, speed: 0.5 },
      flicker: { alpha: 0.55, scale: 0.7, duration: 300 },
    }),
    orbiter({
      radius: 3, color: 0xFFAA33, alpha: 1, glow: true,
      orbit: { radius: 24, speed: -0.7, startAngle: Math.PI },
      flicker: { alpha: 0.45, duration: 240 },
    }),
    // Rising heat sparks
    driftMotes({
      direction: 'up', count: 4, spread: 16, radius: 3,
      color: 0xFFD966, alpha: 1, depth: 53, glow: true,
      travel: 46, duration: 700, delay: 120,
    }),
  ],
};

const STATUS_POISON: VFXEffectConfig = {
  id: 'status_poison',
  name: 'Poisoned',
  category: 'status',
  description: 'Sickly bubbles dripping off the poison DoT victim',
  followTarget: 'player',
  primitives: [
    // Sickly haze
    pulsingShell({
      width: 56, height: 60, color: 0x44CC44, alpha: 0.13,
      pulse: { scale: 1.08, duration: 900 },
    }),
    // Dripping blobs (fall + fade, staggered)
    driftMotes({
      direction: 'down', count: 3, spread: 18, radius: 3,
      color: 0x66DD44, alpha: 0.85, travel: 38, duration: 850,
    }),
    driftMotes({
      direction: 'down', count: 2, spread: 12, radius: 2,
      color: 0x99FF66, alpha: 0.75, glow: true, travel: 30, duration: 1050, delay: 400,
    }),
    // Bubble pop at the shoulder line (bespoke one-off — stays literal)
    {
      type: 'circle', radius: 4, color: 0x88FF66, alpha: 0.5, depth: 51,
      stroke: { color: 0xBBFF99, width: 1 },
      tween: { scale: 1.8, alpha: 0, duration: 620, repeat: -1, delay: 200 },
    },
  ],
};

const STATUS_SLOW: VFXEffectConfig = {
  id: 'status_slow',
  name: 'Slowed',
  category: 'status',
  description: 'Heavy drag ring at the feet — deliberately sluggish tweens',
  followTarget: 'player',
  primitives: [
    // Squashed weight ring at the feet (the "wading through mud" read)
    groundRing({
      width: 64, height: 20, color: 0x66CCFF, alpha: 0.28,
      stroke: { color: 0x99DDFF, width: 2 },
      pulse: { scale: 1.06, alpha: 0.18, duration: 1400 },
    }),
    // Slow-orbiting drag shards — long period sells the sluggishness
    orbiter({
      shape: 'rect', width: 7, height: 3, color: 0x88CCEE, alpha: 0.7, depth: 51,
      orbit: { radius: 30, speed: 0.15, orientToPath: true },
    }),
    orbiter({
      shape: 'rect', width: 5, height: 2, color: 0xAADDFF, alpha: 0.6, depth: 51,
      orbit: { radius: 34, speed: -0.12, startAngle: Math.PI * 0.7, orientToPath: true },
    }),
  ],
};

const STATUS_FROZEN: VFXEffectConfig = {
  id: 'status_frozen',
  name: 'Frozen',
  category: 'status',
  description: 'Crystalline shard shell + glinting highlights',
  followTarget: 'player',
  primitives: [
    // Icy shell
    pulsingShell({
      width: 58, height: 68, color: 0x99DDFF, alpha: 0.22,
      stroke: { color: 0xCCEEFF, width: 2 },
      pulse: { alpha: 0.14, duration: 1100 },
    }),
    // Ice shards ringing the target (bespoke crystal read — stays literal)
    {
      type: 'polygon', polygonPoints: [0, -9, 3, 0, -3, 0], color: 0xBBE8FF, alpha: 0.9, depth: 52,
      stroke: { color: 0xFFFFFF, width: 1 },
      scatter: { count: 6, radius: 32 },
    },
    // Glints — brief white flashes (bespoke — stays literal)
    {
      type: 'star', radius: 3, color: 0xFFFFFF, alpha: 0.95, depth: 53, glow: true,
      scatter: { count: 2, radius: 22 },
      tween: { alpha: 0, scale: 0.4, duration: 520, yoyo: true, repeat: -1, delay: 260 },
    },
  ],
};

const STATUS_STUN: VFXEffectConfig = {
  id: 'status_stun',
  name: 'Stunned',
  category: 'status',
  description: 'Classic orbiting stars over the head + dizzy ring',
  followTarget: 'player',
  primitives: [
    // Dizzy ring above the head
    pulsingShell({
      width: 40, height: 12, offsetY: -48, depth: 52,
      color: 0xFFDD44, alpha: 0.3,
      pulse: { scale: 1.1, duration: 600 },
    }),
    // Three orbiting stars (offset start angles make the classic carousel)
    orbiter({
      shape: 'star', radius: 5, color: 0xFFDD44, alpha: 0.95, depth: 53, offsetY: -48, glow: true,
      orbit: { radius: 20, speed: 0.9 },
    }),
    orbiter({
      shape: 'star', radius: 4, color: 0xFFEE88, alpha: 0.9, depth: 53, offsetY: -48, glow: true,
      orbit: { radius: 20, speed: 0.9, startAngle: (Math.PI * 2) / 3 },
    }),
    orbiter({
      shape: 'star', radius: 4, color: 0xFFCC22, alpha: 0.9, depth: 53, offsetY: -48, glow: true,
      orbit: { radius: 20, speed: 0.9, startAngle: (Math.PI * 4) / 3 },
    }),
  ],
};

const STATUS_REGEN: VFXEffectConfig = {
  id: 'status_regen',
  name: 'Regenerating',
  category: 'status',
  description: 'Soft green glow with life motes rising off the target',
  followTarget: 'player',
  primitives: [
    // Soft base glow at the feet (VQA 2026-07-17: raised — see burn note)
    groundRing({
      width: 52, height: 18, color: 0x44FF88, alpha: 0.38, glow: true,
      stroke: { color: 0x88FFBB, width: 2 },
      pulse: { alpha: 0.22, scale: 1.08, duration: 800 },
    }),
    // Rising life motes (staggered columns)
    driftMotes({
      direction: 'up', count: 4, spread: 16, radius: 3.5,
      color: 0x66FFAA, alpha: 1, glow: true, travel: 50, duration: 900,
    }),
    driftMotes({
      direction: 'up', count: 2, spread: 10, radius: 4,
      color: 0xAAFFCC, alpha: 0.95, glow: true, travel: 40, duration: 1150, delay: 450,
    }),
  ],
};

// =============================================================================
// Warlock affliction family (F6 expansion, 2026-07-17) — the live
// `sendServerStatus` lane applies agony/corruption/immolate/
// unstable_affliction/curse_of_elements on VICTIMS, and none had a visual:
// the raw ids resolved to nothing on every client. Shared affliction gloom,
// distinct silhouettes. `immolate` is a true burn — it aliases status_burn
// via STATUS_AURA_CONFIG rather than pretending to be a new look.
// =============================================================================

const STATUS_AGONY: VFXEffectConfig = {
  id: 'status_agony',
  name: 'Agony',
  category: 'status',
  description: 'Throbbing dark-red pain pulse — a heartbeat of hurt',
  followTarget: 'player',
  primitives: [
    // Fast double-thump shell — the heartbeat read (short duration, deep red)
    pulsingShell({
      width: 52, height: 62, color: 0xAA1122, alpha: 0.34, glow: true,
      stroke: { color: 0xDD3344, width: 2 },
      pulse: { scale: 1.16, alpha: 0.16, duration: 260 },
    }),
    // Pain sparks wrenched off the body, sinking
    driftMotes({
      direction: 'down', count: 3, spread: 14, radius: 2.5,
      color: 0xCC2233, alpha: 0.95, glow: true, travel: 26, duration: 620,
    }),
    orbiter({
      radius: 3, color: 0x660011, alpha: 0.9,
      orbit: { radius: 26, speed: 0.8 },
      flicker: { alpha: 0.4, duration: 200 },
    }),
  ],
};

const STATUS_CORRUPTION: VFXEffectConfig = {
  id: 'status_corruption',
  name: 'Corruption',
  category: 'status',
  description: 'Creeping violet-black rot with rising shadow wisps',
  followTarget: 'player',
  primitives: [
    pulsingShell({
      width: 58, height: 66, color: 0x551188, alpha: 0.3, glow: true,
      stroke: { color: 0x8833BB, width: 2 },
      pulse: { scale: 1.06, alpha: 0.18, duration: 1000 },
    }),
    // Shadow wisps crawling UP the body — corruption spreads
    driftMotes({
      direction: 'up', count: 4, spread: 18, radius: 3,
      color: 0x7722AA, alpha: 0.9, glow: true, travel: 40, duration: 1100,
    }),
    driftMotes({
      direction: 'up', count: 2, spread: 10, radius: 2,
      color: 0x330055, alpha: 0.85, travel: 32, duration: 1400, delay: 550,
    }),
  ],
};

const STATUS_UNSTABLE_AFFLICTION: VFXEffectConfig = {
  id: 'status_unstable_affliction',
  name: 'Unstable Affliction',
  category: 'status',
  description: 'Volatile magenta instability — fast, erratic, about to pop',
  followTarget: 'player',
  primitives: [
    // Jittery shell: fast shallow pulse sells "barely contained"
    pulsingShell({
      width: 54, height: 62, color: 0xDD22AA, alpha: 0.3, glow: true,
      stroke: { color: 0xFF66DD, width: 2 },
      pulse: { scale: 1.05, alpha: 0.2, duration: 160 },
    }),
    // Counter-rotating volatile sparks, fast and bright
    orbiter({
      radius: 3, color: 0xFF44CC, alpha: 1, glow: true,
      orbit: { radius: 28, speed: 1.4 },
      flicker: { alpha: 0.5, scale: 0.6, duration: 140 },
    }),
    orbiter({
      radius: 2.5, color: 0xFFAAEE, alpha: 1, glow: true,
      orbit: { radius: 22, speed: -1.8, startAngle: Math.PI * 0.5 },
      flicker: { alpha: 0.45, duration: 170 },
    }),
  ],
};

const STATUS_CURSE_OF_ELEMENTS: VFXEffectConfig = {
  id: 'status_curse_of_elements',
  name: 'Curse of the Elements',
  category: 'status',
  description: 'Fire, frost and nature marks circling the cursed — vulnerable to ALL of it',
  followTarget: 'player',
  primitives: [
    // Neutral curse gloom underneath
    pulsingShell({
      width: 56, height: 64, color: 0x6644AA, alpha: 0.22,
      stroke: { color: 0x9977CC, width: 2 },
      pulse: { alpha: 0.14, duration: 900 },
    }),
    // The element ring — each mark keeps its own school hue (palette lock
    // guarantees the mixed carousel never flattens to one tint).
    // VQA 2026-07-17: marks enlarged r3.5→5 + full alpha — the first pass
    // hid behind the sprite at gameplay zoom.
    ...carousel({
      items: [
        { color: 0xFF4500, radius: 5, alpha: 1 }, // fire
        { color: 0x66CCFF, radius: 5, alpha: 1 }, // frost
        { color: 0x44CC44, radius: 5, alpha: 1 }, // nature
      ],
      orbitRadius: 34, speed: 0.6, glow: true,
      flicker: { alpha: 0.7, duration: 300 },
    }),
  ],
};

const STATUS_HASTE: VFXEffectConfig = {
  id: 'status_haste',
  name: 'Hastened',
  category: 'status',
  description: 'Speed streaks trailing the accelerated runner (sprint/haste family)',
  followTarget: 'player',
  primitives: [
    // Momentum shell — squashed horizontally, leaning into the run
    pulsingShell({
      width: 62, height: 50, color: 0x00FFAA, alpha: 0.2, glow: true,
      pulse: { scale: 1.1, alpha: 0.12, duration: 300 },
    }),
    // Trailing speed streaks — horizontal dashes flickering behind the body
    orbiter({
      shape: 'rect', width: 14, height: 2, color: 0x66FFCC, alpha: 0.9, glow: true,
      orbit: { radius: 24, speed: 1.6, orientToPath: true },
      flicker: { alpha: 0.35, duration: 180 },
    }),
    orbiter({
      shape: 'rect', width: 10, height: 2, color: 0xAAFFDD, alpha: 0.8,
      orbit: { radius: 30, speed: 1.6, startAngle: Math.PI, orientToPath: true },
      flicker: { alpha: 0.3, duration: 220 },
    }),
  ],
};

// Consumable aura family. Speed and HoT intentionally reuse haste/regen;
// these five cover the remaining item buffs with distinct silhouettes.
const STATUS_CONSUMABLE_FORTITUDE: VFXEffectConfig = {
  id: 'status_consumable_fortitude',
  name: 'Fortitude Elixir',
  category: 'status',
  description: 'Protective heart motes orbit a sturdy golden life shell',
  followTarget: 'player',
  primitives: [
    pulsingShell({
      width: 60, height: 70, color: 0xEAB308, alpha: 0.22, glow: true,
      stroke: { color: 0xFDE68A, width: 2 },
      pulse: { scale: 1.08, alpha: 0.14, duration: 900 },
    }),
    orbiter({
      shape: 'star', radius: 4, color: 0xFDE68A, alpha: 0.95, glow: true,
      orbit: { radius: 34, speed: 0.42 },
      flicker: { alpha: 0.55, duration: 420 },
    }),
  ],
};

const STATUS_CONSUMABLE_TITANS: VFXEffectConfig = {
  id: 'status_consumable_titans',
  name: 'Flask of the Titans',
  category: 'status',
  description: 'Four primary-stat lights circle a tall prismatic shell',
  followTarget: 'player',
  primitives: [
    pulsingShell({
      width: 66, height: 78, color: 0xA855F7, alpha: 0.2, glow: true,
      stroke: { color: 0xE9D5FF, width: 2 },
      pulse: { scale: 1.12, alpha: 0.12, duration: 1200 },
    }),
    ...carousel({
      items: [
        { color: 0xEF4444, radius: 4.5, alpha: 1 },
        { color: 0x22C55E, radius: 4.5, alpha: 1 },
        { color: 0x3B82F6, radius: 4.5, alpha: 1 },
        { color: 0xFACC15, radius: 4.5, alpha: 1 },
      ],
      orbitRadius: 38,
      speed: 0.5,
      glow: true,
    }),
  ],
};

const STATUS_CONSUMABLE_ATTACK: VFXEffectConfig = {
  id: 'status_consumable_attack',
  name: 'Attack Scroll',
  category: 'status',
  description: 'Fast crimson blade marks orbit the empowered attacker',
  followTarget: 'player',
  primitives: [
    groundRing({
      width: 58, height: 16, color: 0xDC2626, alpha: 0.28,
      stroke: { color: 0xFCA5A5, width: 2 },
      pulse: { scale: 1.14, alpha: 0.16, duration: 460 },
    }),
    orbiter({
      shape: 'rect', width: 13, height: 3, color: 0xF87171, alpha: 0.95, glow: true,
      orbit: { radius: 31, speed: 1.1, orientToPath: true },
    }),
  ],
};

const STATUS_CONSUMABLE_DEFENSE: VFXEffectConfig = {
  id: 'status_consumable_defense',
  name: 'Defense Scroll',
  category: 'status',
  description: 'Blue shield facets form a measured protective perimeter',
  followTarget: 'player',
  primitives: [
    pulsingShell({
      width: 62, height: 68, color: 0x2563EB, alpha: 0.2,
      stroke: { color: 0x93C5FD, width: 2 },
      pulse: { alpha: 0.12, duration: 1300 },
    }),
    {
      type: 'polygon',
      polygonPoints: [0, -7, 6, -3, 5, 5, 0, 8, -5, 5, -6, -3],
      color: 0x60A5FA,
      alpha: 0.8,
      depth: 52,
      scatter: { count: 5, radius: 34 },
      tween: { alpha: 0.45, duration: 800, yoyo: true, repeat: -1 },
    },
  ],
};

const STATUS_CONSUMABLE_EXPERIENCE: VFXEffectConfig = {
  id: 'status_consumable_experience',
  name: 'Experience Scroll',
  category: 'status',
  description: 'Knowledge stars rise through a slow violet orbit',
  followTarget: 'player',
  primitives: [
    driftMotes({
      direction: 'up', count: 5, spread: 18, radius: 3,
      color: 0xC084FC, alpha: 0.95, glow: true, travel: 54, duration: 1450,
    }),
    orbiter({
      shape: 'star', radius: 4, color: 0xF5D0FE, alpha: 0.9, glow: true,
      orbit: { radius: 28, speed: 0.28 },
      flicker: { alpha: 0.4, duration: 700 },
    }),
  ],
};

export const STATUS_VFX: VFXEffectConfig[] = [
  STATUS_BURN,
  STATUS_POISON,
  STATUS_SLOW,
  STATUS_FROZEN,
  STATUS_STUN,
  STATUS_REGEN,
  STATUS_AGONY,
  STATUS_CORRUPTION,
  STATUS_UNSTABLE_AFFLICTION,
  STATUS_CURSE_OF_ELEMENTS,
  STATUS_HASTE,
  STATUS_CONSUMABLE_FORTITUDE,
  STATUS_CONSUMABLE_TITANS,
  STATUS_CONSUMABLE_ATTACK,
  STATUS_CONSUMABLE_DEFENSE,
  STATUS_CONSUMABLE_EXPERIENCE,
];
